import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Wallet, TrendingUp, TrendingDown, Save, Plus, Trash2, Edit2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import DatePickerField from '../components/DatePickerField';
import CategorySelect from '../components/CategorySelect';
import PeriodFilter, { applyPeriodFilter } from '../components/PeriodFilter';
import DateRangeFilter, { applyDateRange, formatDateFR } from '../components/DateRangeFilter';
import SearchBox, { applySearch } from '../components/SearchBox';
import DownloadFinancePdfButton from '../components/DownloadFinancePdfButton';

const formatCR = (n) => {
  const v = Math.round(parseFloat(n) || 0);
  return v.toLocaleString('fr-FR');
};

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

const CHARGE_CATEGORIES = [
  'Achat panneaux & matériel', 'Transport & douanes', 'Loyer et charges',
  'Maintenance panneaux', 'Carburant & véhicules', 'Salaires',
  'Charges sociales (CNSS)', 'Mairie / Autorisations', 'Assurances',
  'Frais bancaires', 'Honoraires (comptable, avocat)', 'Télécom & internet',
  'Marketing & publicité', 'Fournitures de bureau', 'Formation',
  'Dividendes investisseurs (20%)', 'Amortissements', 'Frais financiers',
  'Impôts et taxes', 'Divers',
];

const PRODUIT_CATEGORIES = [
  'Location panneau (mensuel)', 'Location panneau (trimestriel)',
  'Location panneau (annuel)', 'Installation & pose', 'Impression bâche',
  'Conception graphique', 'Frais de dossier', 'Frais de retrait',
  'Refacturation taxes mairie', 'Indemnités assurance', 'Cession actif',
  'Produits financiers', 'Divers',
];

const today = () => new Date().toISOString().split('T')[0];
const newRowEnc = () => ({ _draftId: `d${Date.now()}${Math.random()}`, date: today(), categorie: 'Location panneau (mensuel)', description: '', montant: 0 });
const newRowDec = () => ({ _draftId: `d${Date.now()}${Math.random()}`, date: today(), categorie: 'Loyer et charges', description: '', montant: 0 });

export default function TableauTresorerie({ user, onLogout }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Transactions brutes (credits = encaissements, debits = décaissements)
  const [credits, setCredits] = useState([]);
  const [debits, setDebits] = useState([]);

  // Brouillons de nouvelles lignes
  const [encDrafts, setEncDrafts] = useState([newRowEnc()]);
  const [decDrafts, setDecDrafts] = useState([newRowDec()]);

  // Sélecteur période rapide pour filtrer les lignes enregistrées
  const [encPeriod, setEncPeriod] = useState('all');
  const [decPeriod, setDecPeriod] = useState('all');

  // Filtre par intervalle de dates pour le tableau bilatéral (en haut)
  const [bilatDateFrom, setBilatDateFrom] = useState('');
  const [bilatDateTo, setBilatDateTo] = useState('');
  // Recherche par libellé / numéro facture
  const [search, setSearch] = useState('');

  // Quick-add dialogs (formulaires Entrée/Sortie en haut à droite)
  const [quickEnc, setQuickEnc] = useState(null); // null | { date, categorie, description, montant }
  const [quickDec, setQuickDec] = useState(null);
  const [savingQuick, setSavingQuick] = useState(false);
  const openQuickEnc = () => setQuickEnc({ date: today(), categorie: 'Location panneau (mensuel)', description: '', montant: 0 });
  const openQuickDec = () => setQuickDec({ date: today(), categorie: 'Loyer et charges', description: '', montant: 0 });
  const submitQuick = async (type) => {
    const data = type === 'enc' ? quickEnc : quickDec;
    if (!data || !((parseFloat(data.montant) || 0) > 0)) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }
    setSavingQuick(true);
    try {
      if (type === 'enc') {
        await axios.post(`${API}/api/financial/credit`, {
          date: data.date,
          description: data.description || data.categorie,
          categorie: data.categorie,
          annonceur: data.categorie,
          montant: parseFloat(data.montant) || 0,
        });
      } else {
        await axios.post(`${API}/api/financial/debit`, {
          date: data.date,
          description: data.description || data.categorie,
          categorie: data.categorie,
          statut: 'Payé',
          montant: parseFloat(data.montant) || 0,
        });
      }
      toast.success(`${type === 'enc' ? 'Entrée' : 'Sortie'} enregistrée`);
      if (type === 'enc') setQuickEnc(null); else setQuickDec(null);
      await Promise.all([loadTransactions(), loadTresorerie()]);
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingQuick(false);
    }
  };

  // Ligne en cours d'édition (id de la ligne existante)
  const [editEncId, setEditEncId] = useState(null);
  const [editEncRow, setEditEncRow] = useState(null);
  const [editDecId, setEditDecId] = useState(null);
  const [editDecRow, setEditDecRow] = useState(null);

  // Catégories dynamiques
  const [customChargeCats, setCustomChargeCats] = useState([]);
  const [customProduitCats, setCustomProduitCats] = useState([]);
  const [hiddenChargeDefaults, setHiddenChargeDefaults] = useState([]);
  const [hiddenProduitDefaults, setHiddenProduitDefaults] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/financial/categories`);
      setCustomChargeCats(r.data?.charges || []);
      setCustomProduitCats(r.data?.produits || []);
      setHiddenChargeDefaults(r.data?.hiddenDefaults?.charges || []);
      setHiddenProduitDefaults(r.data?.hiddenDefaults?.produits || []);
    } catch { /* silent */ }
  }, [API]);

  const loadTransactions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/financial/transactions`);
      setCredits(r.data?.credits || []);
      setDebits(r.data?.debits || []);
    } catch {
      toast.error('Erreur chargement transactions');
    }
  }, [API]);

  const loadTresorerie = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/financial/tresorerie?year=${year}`);
      setData(res.data);
    } catch {
      toast.error('Erreur de chargement');
    }
  }, [API, year]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadTresorerie(), loadTransactions(), loadCategories()]);
    setLoading(false);
  }, [loadTresorerie, loadTransactions, loadCategories]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ======================== ENCAISSEMENTS ========================
  const addEncRow = () => setEncDrafts([...encDrafts, newRowEnc()]);
  const updateEncDraft = (id, field, value) =>
    setEncDrafts(encDrafts.map(r => r._draftId === id ? { ...r, [field]: value } : r));
  const removeEncDraft = (id) =>
    setEncDrafts(encDrafts.filter(r => r._draftId !== id));

  const saveEncDrafts = async () => {
    const valid = encDrafts.filter(r => (parseFloat(r.montant) || 0) > 0);
    if (valid.length === 0) { toast.error('Aucun encaissement valide'); return; }
    try {
      for (const r of valid) {
        await axios.post(`${API}/api/financial/credit`, {
          date: r.date,
          description: r.description || r.categorie,
          categorie: r.categorie,
          annonceur: r.categorie,
          montant: parseFloat(r.montant) || 0,
        });
      }
      setEncDrafts([newRowEnc()]);
      toast.success(`${valid.length} encaissement(s) enregistré(s)`);
      await Promise.all([loadTransactions(), loadTresorerie()]);
    } catch {
      toast.error('Erreur enregistrement');
    }
  };

  const startEditEnc = (c) => {
    setEditEncId(c.id);
    setEditEncRow({ date: c.date, categorie: c.categorie || c.annonceur || 'Divers', description: c.description || '', montant: c.montant });
  };
  const cancelEditEnc = () => { setEditEncId(null); setEditEncRow(null); };
  const saveEditEnc = async () => {
    try {
      await axios.put(`${API}/api/financial/credit/${editEncId}`, {
        date: editEncRow.date,
        categorie: editEncRow.categorie,
        annonceur: editEncRow.categorie,
        description: editEncRow.description || editEncRow.categorie,
        montant: parseFloat(editEncRow.montant) || 0,
      });
      toast.success('Encaissement modifié');
      cancelEditEnc();
      await Promise.all([loadTransactions(), loadTresorerie()]);
    } catch {
      toast.error('Erreur modification');
    }
  };
  const deleteEnc = async (id) => {
    if (!window.confirm('Supprimer cet encaissement ?')) return;
    try {
      await axios.delete(`${API}/api/financial/credit/${id}`);
      toast.success('Encaissement supprimé');
      await Promise.all([loadTransactions(), loadTresorerie()]);
    } catch {
      toast.error('Erreur suppression');
    }
  };

  // ======================== DÉCAISSEMENTS ========================
  const addDecRow = () => setDecDrafts([...decDrafts, newRowDec()]);
  const updateDecDraft = (id, field, value) =>
    setDecDrafts(decDrafts.map(r => r._draftId === id ? { ...r, [field]: value } : r));
  const removeDecDraft = (id) =>
    setDecDrafts(decDrafts.filter(r => r._draftId !== id));

  const saveDecDrafts = async () => {
    const valid = decDrafts.filter(r => (parseFloat(r.montant) || 0) > 0);
    if (valid.length === 0) { toast.error('Aucun décaissement valide'); return; }
    try {
      for (const r of valid) {
        await axios.post(`${API}/api/financial/debit`, {
          date: r.date,
          description: r.description || r.categorie,
          categorie: r.categorie,
          statut: 'Payé',
          montant: parseFloat(r.montant) || 0,
        });
      }
      setDecDrafts([newRowDec()]);
      toast.success(`${valid.length} décaissement(s) enregistré(s)`);
      await Promise.all([loadTransactions(), loadTresorerie()]);
    } catch {
      toast.error('Erreur enregistrement');
    }
  };

  const startEditDec = (d) => {
    setEditDecId(d.id);
    setEditDecRow({ date: d.date, categorie: d.categorie || 'Divers', description: d.description || '', montant: d.montant });
  };
  const cancelEditDec = () => { setEditDecId(null); setEditDecRow(null); };
  const saveEditDec = async () => {
    try {
      await axios.put(`${API}/api/financial/debit/${editDecId}`, {
        date: editDecRow.date,
        categorie: editDecRow.categorie,
        description: editDecRow.description || editDecRow.categorie,
        statut: 'Payé',
        montant: parseFloat(editDecRow.montant) || 0,
      });
      toast.success('Décaissement modifié');
      cancelEditDec();
      await Promise.all([loadTransactions(), loadTresorerie()]);
    } catch {
      toast.error('Erreur modification');
    }
  };
  const deleteDec = async (id) => {
    if (!window.confirm('Supprimer ce décaissement ?')) return;
    try {
      await axios.delete(`${API}/api/financial/debit/${id}`);
      toast.success('Décaissement supprimé');
      await Promise.all([loadTransactions(), loadTresorerie()]);
    } catch {
      toast.error('Erreur suppression');
    }
  };

  if (loading || !data) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const {
    solde_initial, total_encaissements, total_decaissements, solde_final,
    encaissements_par_categorie, decaissements_par_categorie,
  } = data;
  const isPositif = solde_final >= 0;

  // Filtre par année pour les tableaux détaillés
  const yearPrefix = String(year);
  const encOfYearAll = credits.filter(c => (c.date || '').startsWith(yearPrefix))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const decOfYearAll = debits
    .filter(d => (d.date || '').startsWith(yearPrefix))
    .filter(d => ((d.statut || '').toLowerCase() === 'payé' || (d.statut || '').toLowerCase() === 'paye' || (d.statut || '').toLowerCase() === 'paid'))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const encOfYear = applySearch(applyPeriodFilter(encOfYearAll, encPeriod), search);
  const decOfYear = applySearch(applyPeriodFilter(decOfYearAll, decPeriod), search);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="tresorerie-page">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={() => navigate('/entreprise?tab=finances')} variant="outline" className="gap-2" data-testid="tresorerie-back-btn">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <DownloadFinancePdfButton
            type="tresorerie"
            dateDebut={`${year}-01-01`}
            dateFin={`${year}-12-31`}
            testId="tresorerie-admin-download-pdf"
          />
          <div className="flex-1">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Wallet className="h-7 w-7 text-blue-600" /> Tableau de Trésorerie
            </h1>
            <p className="text-gray-600 text-sm">Encaissements & décaissements de l'année {year}</p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="flex gap-2">
              <Button onClick={openQuickEnc} className="bg-green-600 hover:bg-green-700 gap-2" data-testid="quick-add-entree-btn">
                <Plus className="h-4 w-4" /> Entrée
              </Button>
              <Button onClick={openQuickDec} className="bg-red-600 hover:bg-red-700 gap-2" data-testid="quick-add-sortie-btn">
                <Plus className="h-4 w-4" /> Sortie
              </Button>
            </div>
            <DateRangeFilter
              dateFrom={bilatDateFrom}
              dateTo={bilatDateTo}
              onChange={({ dateFrom: f, dateTo: t }) => { setBilatDateFrom(f); setBilatDateTo(t); }}
              color="blue"
              testIdPrefix="tresor-range"
            />
            <SearchBox value={search} onChange={setSearch} color="blue" testIdPrefix="tresor-search" />
          </div>
        </div>

        {/* KPI cards (cliquables) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            onClick={() => navigate('/tresorerie/encaissements')}
            className="cursor-pointer border-l-4 border-l-green-500 bg-gradient-to-br from-green-50 to-white hover:shadow-lg hover:scale-[1.02] transition-all"
            data-testid="kpi-encaissements-card"
          >
            <CardContent className="p-4">
              <p className="text-xs uppercase font-semibold text-green-700 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Encaissements
              </p>
              <p className="text-xl font-bold text-green-700 mt-1" data-testid="total-encaissements">
                +{formatCR(total_encaissements)} FCFA
              </p>
              <p className="text-[10px] mt-1 italic text-gray-500">Cliquez pour voir le détail</p>
            </CardContent>
          </Card>

          <Card
            onClick={() => navigate('/tresorerie/decaissements')}
            className="cursor-pointer border-l-4 border-l-red-500 bg-gradient-to-br from-red-50 to-white hover:shadow-lg hover:scale-[1.02] transition-all"
            data-testid="kpi-decaissements-card"
          >
            <CardContent className="p-4">
              <p className="text-xs uppercase font-semibold text-red-700 flex items-center gap-1">
                <TrendingDown className="h-3 w-3" /> Décaissements
              </p>
              <p className="text-xl font-bold text-red-700 mt-1" data-testid="total-decaissements">
                −{formatCR(total_decaissements)} FCFA
              </p>
              <p className="text-[10px] mt-1 italic text-gray-500">Cliquez pour voir le détail</p>
            </CardContent>
          </Card>

          <Card
            onClick={() => navigate('/tresorerie/evolution')}
            className={`cursor-pointer border-l-4 hover:shadow-lg hover:scale-[1.02] transition-all ${
              isPositif ? 'border-l-blue-600 bg-gradient-to-br from-blue-50 to-white' : 'border-l-red-700 bg-gradient-to-br from-red-50 to-white'
            }`}
            data-testid="solde-final-card"
          >
            <CardContent className="p-4">
              <p className={`text-xs uppercase font-semibold ${isPositif ? 'text-blue-700' : 'text-red-700'}`}>
                Solde final
              </p>
              <p className={`text-xl font-bold mt-1 ${isPositif ? 'text-blue-700' : 'text-red-700'}`} data-testid="solde-final">
                {formatCR(solde_final)} FCFA
              </p>
              <p className="text-[10px] mt-1 italic text-gray-500">Cliquez pour voir l'évolution mensuelle</p>
            </CardContent>
          </Card>
        </div>

        {/* Tableau bilatéral Décaissements (gauche) | Encaissements (droite) — style Compte de Résultat */}
        {(() => {
          const bilatPeriodLabel = (bilatDateFrom || bilatDateTo)
            ? `${bilatDateFrom ? formatDateFR(bilatDateFrom) : '—'} → ${bilatDateTo ? formatDateFR(bilatDateTo) : '—'}`
            : '';
          // Encaissements / décaissements filtrés par intervalle + recherche puis groupés par catégorie
          const encFiltered = applySearch(
            applyDateRange(credits, bilatDateFrom, bilatDateTo, 'date')
              .filter(c => String(c.date || '').startsWith(String(year))),
            search
          );
          const decFiltered = applySearch(
            applyDateRange(debits, bilatDateFrom, bilatDateTo, 'date')
              .filter(d => String(d.date || '').startsWith(String(year))),
            search
          );
          const groupByCat = (arr) => {
            const m = {};
            for (const it of arr) {
              const k = it.categorie || it.annonceur || 'Divers';
              if (!m[k]) m[k] = [];
              m[k].push(it);
            }
            return Object.entries(m).map(([categorie, entries]) => ({
              categorie,
              entries: [...entries].sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))),
              total: entries.reduce((s, e) => s + (parseFloat(e.montant) || 0), 0),
            }));
          };
          const encGroupedByCat = groupByCat(encFiltered);
          const decGroupedByCat = groupByCat(decFiltered);
          const totalEncFiltered = encFiltered.reduce((s, e) => s + (parseFloat(e.montant) || 0), 0);
          const totalDecFiltered = decFiltered.reduce((s, e) => s + (parseFloat(e.montant) || 0), 0);
          const soldeBilat = (solde_initial || 0) + totalEncFiltered - totalDecFiltered;
          const isPositifBilat = soldeBilat >= 0;
          return (
        <Card className="shadow-lg border-2 border-blue-100" data-testid="tresorerie-bilat">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardTitle className="flex items-center justify-between flex-wrap gap-2">
              <span className="flex items-center gap-2">
                <Wallet className="h-6 w-6" />
                Flux de trésorerie — {bilatPeriodLabel || year}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Décaissements à gauche */}
              <div className="border-r border-gray-200">
                <div className="bg-red-600 text-white px-6 py-3">
                  <h3 className="font-bold text-lg uppercase tracking-wide flex items-center gap-2">
                    <TrendingDown className="h-5 w-5" /> Décaissements (Sorties)
                  </h3>
                </div>
                <table className="w-full">
                  <thead className="bg-red-50 border-b border-red-200">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-semibold text-red-800 uppercase tracking-wider">Libellé</th>
                      <th className="px-6 py-2 text-right text-xs font-semibold text-red-800 uppercase tracking-wider">Montant (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {decGroupedByCat.length === 0 ? (
                      <tr>
                        <td className="px-6 py-3 text-gray-400 italic">Aucun décaissement</td>
                        <td className="px-6 py-3 text-right text-gray-400">0</td>
                      </tr>
                    ) : (
                      decGroupedByCat.map((g) => (
                        <React.Fragment key={`dec-${g.categorie}`}>
                          <tr className="bg-red-100/70">
                            <td colSpan={2} className="px-4 py-2 text-xs font-bold text-red-900 uppercase tracking-wide">
                              {g.categorie}
                            </td>
                          </tr>
                          {g.entries.map((e, i) => (
                            <tr key={`dec-${g.categorie}-${i}`} className="hover:bg-red-50/50">
                              <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                                <span className="text-gray-900 font-mono text-xs mr-2">{formatDateFR(e.date)}</span>
                                {e.libelle || e.description || g.categorie}
                              </td>
                              <td className="px-6 py-2 text-right text-red-700 font-medium">{formatCR(e.montant)}</td>
                            </tr>
                          ))}
                          <tr className="bg-red-50/60 border-t border-red-200">
                            <td className="px-6 py-2 pl-10 text-xs italic text-red-800">Total {g.categorie}</td>
                            <td className="px-6 py-2 text-right text-red-800 font-bold">{formatCR(g.total)}</td>
                          </tr>
                        </React.Fragment>
                      ))
                    )}
                    {isPositifBilat && soldeBilat > 0 && (
                      <tr className="bg-blue-50 border-t border-blue-200">
                        <td className="px-6 py-3 italic text-blue-700 font-medium">Excédent de trésorerie</td>
                        <td className="px-6 py-3 text-right text-blue-700 font-bold">{formatCR(soldeBilat)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Encaissements à droite */}
              <div>
                <div className="bg-green-600 text-white px-6 py-3">
                  <h3 className="font-bold text-lg uppercase tracking-wide flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" /> Encaissements (Entrées)
                  </h3>
                </div>
                <table className="w-full">
                  <thead className="bg-green-50 border-b border-green-200">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-semibold text-green-800 uppercase tracking-wider">Libellé</th>
                      <th className="px-6 py-2 text-right text-xs font-semibold text-green-800 uppercase tracking-wider">Montant (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-green-100">
                    {solde_initial > 0 && !(bilatDateFrom || bilatDateTo) && (
                      <>
                        <tr className="bg-gray-100/70">
                          <td colSpan={2} className="px-4 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
                            Solde initial de trésorerie
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50/50">
                          <td className="px-6 py-2 pl-10 text-gray-700 text-sm">Report au 1er janvier {year}</td>
                          <td className="px-6 py-2 text-right text-gray-700 font-medium">{formatCR(solde_initial)}</td>
                        </tr>
                      </>
                    )}
                    {encGroupedByCat.length === 0 ? (
                      <tr>
                        <td className="px-6 py-3 text-gray-400 italic">Aucun encaissement</td>
                        <td className="px-6 py-3 text-right text-gray-400">0</td>
                      </tr>
                    ) : (
                      encGroupedByCat.map((g) => (
                        <React.Fragment key={`enc-${g.categorie}`}>
                          <tr className="bg-green-100/70">
                            <td colSpan={2} className="px-4 py-2 text-xs font-bold text-green-900 uppercase tracking-wide">
                              {g.categorie}
                            </td>
                          </tr>
                          {g.entries.map((e, i) => (
                            <tr key={`enc-${g.categorie}-${i}`} className="hover:bg-green-50/50">
                              <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                                <span className="text-gray-900 font-mono text-xs mr-2">{formatDateFR(e.date)}</span>
                                {e.libelle || e.description || g.categorie}
                              </td>
                              <td className="px-6 py-2 text-right text-green-700 font-medium">{formatCR(e.montant)}</td>
                            </tr>
                          ))}
                          <tr className="bg-green-50/60 border-t border-green-200">
                            <td className="px-6 py-2 pl-10 text-xs italic text-green-800">Total {g.categorie}</td>
                            <td className="px-6 py-2 text-right text-green-800 font-bold">{formatCR(g.total)}</td>
                          </tr>
                        </React.Fragment>
                      ))
                    )}
                    {!isPositifBilat && soldeBilat < 0 && (
                      <tr className="bg-orange-50 border-t border-orange-200">
                        <td className="px-6 py-3 italic text-orange-700 font-medium">Déficit de trésorerie</td>
                        <td className="px-6 py-3 text-right text-orange-700 font-bold">{formatCR(Math.abs(soldeBilat))}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 bg-gradient-to-r from-red-100 via-gray-100 to-green-100 border-t-2 border-b-2 border-gray-300 font-bold">
              <div className="px-6 py-4 flex items-center justify-between border-r border-gray-300">
                <span className="text-red-900 uppercase tracking-wide">Total décaissements</span>
                <span className="text-red-900 text-xl" data-testid="tot-dec">
                  {formatCR(totalDecFiltered + (isPositifBilat ? soldeBilat : 0))} FCFA
                </span>
              </div>
              <div className="px-6 py-4 flex items-center justify-between">
                <span className="text-green-900 uppercase tracking-wide">Total encaissements</span>
                <span className="text-green-900 text-xl" data-testid="tot-enc">
                  {formatCR(totalEncFiltered + (!(bilatDateFrom || bilatDateTo) ? solde_initial : 0) + (!isPositifBilat ? Math.abs(soldeBilat) : 0))} FCFA
                </span>
              </div>
            </div>

            <div className={`px-6 py-5 text-center font-bold text-lg ${isPositifBilat ? 'bg-blue-600 text-white' : 'bg-red-700 text-white'}`}>
              Solde {bilatPeriodLabel ? 'de la période' : 'final'} : {formatCR(soldeBilat)} FCFA {isPositifBilat ? '(Excédent)' : '(Déficit)'}
            </div>
          </CardContent>
        </Card>
          );
        })()}

        {/* ======================== TABLEAU ENTRÉES (saisie) ======================== */}
        <Card className="border-2 border-green-200" data-testid="encaissements-table-card">
          <CardHeader className="bg-green-50 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-green-700 text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Tableau des Entrées (Encaissements) — {encOfYear.length}/{encOfYearAll.length}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <PeriodFilter value={encPeriod} onChange={setEncPeriod} color="green" testIdPrefix="enc-period" />
              <Button size="sm" variant="outline" onClick={addEncRow} className="gap-1 border-green-500 text-green-700 hover:bg-green-50" data-testid="add-enc-row-btn">
                <Plus className="h-3 w-3" /> Entrée
              </Button>
              <Button size="sm" onClick={saveEncDrafts} className="bg-green-600 hover:bg-green-700 gap-1" data-testid="save-enc-btn">
                <Save className="h-3 w-3" /> Enregistrer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-100 text-green-900">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                    <th className="px-3 py-2 text-left font-semibold">Description</th>
                    <th className="px-3 py-2 text-right font-semibold">Montant (FCFA)</th>
                    <th className="px-3 py-2 w-24 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {encOfYear.length === 0 && encDrafts.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-gray-400 py-6 text-sm">Aucun encaissement pour cette période</td>
                    </tr>
                  )}

                  {/* Brouillons (en haut) */}
                  {encDrafts.map((row) => (
                    <tr key={`enc-draft-${row._draftId}`} className="border-b bg-green-50/30 hover:bg-green-50">
                      <td className="px-2 py-1">
                        <DatePickerField
                          value={row.date}
                          onChange={(v) => updateEncDraft(row._draftId, 'date', v)}
                          size="sm"
                          className="border-green-200"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <CategorySelect
                          value={row.categorie}
                          onChange={(v) => updateEncDraft(row._draftId, 'categorie', v)}
                          type="produit"
                          defaultCategories={PRODUIT_CATEGORIES}
                          hiddenDefaults={hiddenProduitDefaults}
                          customCategories={customProduitCats}
                          onCategoriesChange={(nc, nh) => { setCustomProduitCats(nc); if (nh !== undefined) setHiddenProduitDefaults(nh); }}
                          triggerClassName="h-8 border-green-200"
                          testId={`enc-draft-cat-${row._draftId}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          value={row.description}
                          onChange={(e) => updateEncDraft(row._draftId, 'description', e.target.value)}
                          placeholder="Description..."
                          className="h-8 border-green-200"
                          data-testid={`enc-draft-desc-${row._draftId}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          type="number"
                          value={row.montant}
                          onChange={(e) => updateEncDraft(row._draftId, 'montant', parseFloat(e.target.value) || 0)}
                          className="h-8 border-green-200 text-right font-semibold text-green-700"
                          min="0" step="0.01"
                          data-testid={`enc-draft-montant-${row._draftId}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <div className="flex items-center justify-center">
                          <Button size="sm" variant="ghost" onClick={() => removeEncDraft(row._draftId)}
                                  className="h-8 w-8 p-0 text-red-500 hover:bg-red-100" title="Retirer la ligne">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* Séparateur */}
                  {encOfYear.length > 0 && encDrafts.length > 0 && (
                    <tr className="bg-green-50/60">
                      <td colSpan="5" className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-green-700 border-t border-b border-green-200">
                        Encaissements déjà enregistrés
                      </td>
                    </tr>
                  )}

                  {/* Lignes enregistrées */}
                  {encOfYear.map((c) => {
                    const isEditing = editEncId === c.id;
                    return (
                      <tr key={`enc-${c.id}`} className="border-b bg-white hover:bg-green-50/60">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {isEditing ? (
                            <DatePickerField value={editEncRow.date}
                              onChange={(v) => setEditEncRow({ ...editEncRow, date: v })}
                              size="sm" className="border-green-200" />
                          ) : (
                            new Date(c.date).toLocaleDateString('fr-FR')
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <CategorySelect
                              value={editEncRow.categorie}
                              onChange={(v) => setEditEncRow({ ...editEncRow, categorie: v })}
                              type="produit"
                              defaultCategories={PRODUIT_CATEGORIES}
                              hiddenDefaults={hiddenProduitDefaults}
                              customCategories={customProduitCats}
                              onCategoriesChange={(nc, nh) => { setCustomProduitCats(nc); if (nh !== undefined) setHiddenProduitDefaults(nh); }}
                              triggerClassName="h-8 border-green-200"
                            />
                          ) : (
                            <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-green-500 text-white">
                              {c.categorie || c.annonceur || 'Divers'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {isEditing ? (
                            <Input value={editEncRow.description}
                              onChange={(e) => setEditEncRow({ ...editEncRow, description: e.target.value })}
                              className="h-8 border-green-200" placeholder="Description..." />
                          ) : (
                            <span className="text-xs">{c.description || '—'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-green-700 whitespace-nowrap">
                          {isEditing ? (
                            <Input type="number" value={editEncRow.montant}
                              onChange={(e) => setEditEncRow({ ...editEncRow, montant: parseFloat(e.target.value) || 0 })}
                              className="h-8 text-right border-green-200" min="0" step="0.01" />
                          ) : (
                            <>+{(parseFloat(c.montant) || 0).toLocaleString('fr-FR')}</>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-center gap-1">
                            {isEditing ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={saveEditEnc}
                                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-100" title="Valider">
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={cancelEditEnc}
                                        className="h-8 w-8 p-0 text-gray-600 hover:bg-gray-100" title="Annuler">
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => startEditEnc(c)}
                                        className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                                        data-testid={`edit-enc-${c.id}`} title="Modifier">
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteEnc(c.id)}
                                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                                        data-testid={`delete-enc-${c.id}`} title="Supprimer">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-green-100 font-semibold">
                  <tr>
                    <td colSpan="3" className="px-3 py-2 text-right text-green-900">TOTAL ENTRÉES :</td>
                    <td className="px-3 py-2 text-right text-green-700 text-base">
                      +{(
                        encOfYear.reduce((s, c) => s + (parseFloat(c.montant) || 0), 0) +
                        encDrafts.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0)
                      ).toLocaleString('fr-FR')} FCFA
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* ======================== TABLEAU SORTIES (saisie) ======================== */}
        <Card className="border-2 border-red-200" data-testid="decaissements-table-card">
          <CardHeader className="bg-red-50 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-red-700 text-lg flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              Tableau des Sorties (Décaissements) — {decOfYear.length}/{decOfYearAll.length}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <PeriodFilter value={decPeriod} onChange={setDecPeriod} color="red" testIdPrefix="dec-period" />
              <Button size="sm" variant="outline" onClick={addDecRow} className="gap-1 border-red-500 text-red-700 hover:bg-red-50" data-testid="add-dec-row-btn">
                <Plus className="h-3 w-3" /> Sortie
              </Button>
              <Button size="sm" onClick={saveDecDrafts} className="bg-red-600 hover:bg-red-700 gap-1" data-testid="save-dec-btn">
                <Save className="h-3 w-3" /> Enregistrer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-100 text-red-900">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                    <th className="px-3 py-2 text-left font-semibold">Description</th>
                    <th className="px-3 py-2 text-right font-semibold">Montant (FCFA)</th>
                    <th className="px-3 py-2 w-24 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {decOfYear.length === 0 && decDrafts.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-gray-400 py-6 text-sm">Aucun décaissement pour cette période</td>
                    </tr>
                  )}

                  {/* Brouillons */}
                  {decDrafts.map((row) => (
                    <tr key={`dec-draft-${row._draftId}`} className="border-b bg-red-50/30 hover:bg-red-50">
                      <td className="px-2 py-1">
                        <DatePickerField
                          value={row.date}
                          onChange={(v) => updateDecDraft(row._draftId, 'date', v)}
                          size="sm"
                          className="border-red-200"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <CategorySelect
                          value={row.categorie}
                          onChange={(v) => updateDecDraft(row._draftId, 'categorie', v)}
                          type="charge"
                          defaultCategories={CHARGE_CATEGORIES}
                          hiddenDefaults={hiddenChargeDefaults}
                          customCategories={customChargeCats}
                          onCategoriesChange={(nc, nh) => { setCustomChargeCats(nc); if (nh !== undefined) setHiddenChargeDefaults(nh); }}
                          triggerClassName="h-8 border-red-200"
                          testId={`dec-draft-cat-${row._draftId}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          value={row.description}
                          onChange={(e) => updateDecDraft(row._draftId, 'description', e.target.value)}
                          placeholder="Description..."
                          className="h-8 border-red-200"
                          data-testid={`dec-draft-desc-${row._draftId}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          type="number"
                          value={row.montant}
                          onChange={(e) => updateDecDraft(row._draftId, 'montant', parseFloat(e.target.value) || 0)}
                          className="h-8 border-red-200 text-right font-semibold text-red-700"
                          min="0" step="0.01"
                          data-testid={`dec-draft-montant-${row._draftId}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <div className="flex items-center justify-center">
                          <Button size="sm" variant="ghost" onClick={() => removeDecDraft(row._draftId)}
                                  className="h-8 w-8 p-0 text-red-500 hover:bg-red-100" title="Retirer la ligne">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* Séparateur */}
                  {decOfYear.length > 0 && decDrafts.length > 0 && (
                    <tr className="bg-red-50/60">
                      <td colSpan="5" className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-red-700 border-t border-b border-red-200">
                        Décaissements déjà enregistrés
                      </td>
                    </tr>
                  )}

                  {/* Lignes enregistrées */}
                  {decOfYear.map((d) => {
                    const isEditing = editDecId === d.id;
                    return (
                      <tr key={`dec-${d.id}`} className="border-b bg-white hover:bg-red-50/60">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {isEditing ? (
                            <DatePickerField value={editDecRow.date}
                              onChange={(v) => setEditDecRow({ ...editDecRow, date: v })}
                              size="sm" className="border-red-200" />
                          ) : (
                            new Date(d.date).toLocaleDateString('fr-FR')
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <CategorySelect
                              value={editDecRow.categorie}
                              onChange={(v) => setEditDecRow({ ...editDecRow, categorie: v })}
                              type="charge"
                              defaultCategories={CHARGE_CATEGORIES}
                              hiddenDefaults={hiddenChargeDefaults}
                              customCategories={customChargeCats}
                              onCategoriesChange={(nc, nh) => { setCustomChargeCats(nc); if (nh !== undefined) setHiddenChargeDefaults(nh); }}
                              triggerClassName="h-8 border-red-200"
                            />
                          ) : (
                            <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-red-500 text-white">
                              {d.categorie || 'Divers'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {isEditing ? (
                            <Input value={editDecRow.description}
                              onChange={(e) => setEditDecRow({ ...editDecRow, description: e.target.value })}
                              className="h-8 border-red-200" placeholder="Description..." />
                          ) : (
                            <span className="text-xs">{d.description || '—'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-red-700 whitespace-nowrap">
                          {isEditing ? (
                            <Input type="number" value={editDecRow.montant}
                              onChange={(e) => setEditDecRow({ ...editDecRow, montant: parseFloat(e.target.value) || 0 })}
                              className="h-8 text-right border-red-200" min="0" step="0.01" />
                          ) : (
                            <>−{(parseFloat(d.montant) || 0).toLocaleString('fr-FR')}</>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-center gap-1">
                            {isEditing ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={saveEditDec}
                                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-100" title="Valider">
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={cancelEditDec}
                                        className="h-8 w-8 p-0 text-gray-600 hover:bg-gray-100" title="Annuler">
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => startEditDec(d)}
                                        className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                                        data-testid={`edit-dec-${d.id}`} title="Modifier">
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteDec(d.id)}
                                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                                        data-testid={`delete-dec-${d.id}`} title="Supprimer">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-red-100 font-semibold">
                  <tr>
                    <td colSpan="3" className="px-3 py-2 text-right text-red-900">TOTAL SORTIES :</td>
                    <td className="px-3 py-2 text-right text-red-700 text-base">
                      −{(
                        decOfYear.reduce((s, d) => s + (parseFloat(d.montant) || 0), 0) +
                        decDrafts.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0)
                      ).toLocaleString('fr-FR')} FCFA
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="px-4 py-2 text-[11px] text-gray-500 italic border-t bg-gray-50">
              Seuls les décaissements de statut « Payé » sont comptabilisés dans le flux de trésorerie. Les nouveaux décaissements ajoutés ici sont automatiquement marqués « Payé ».
            </p>
          </CardContent>
        </Card>

        {/* Dialog Quick-Add ENTRÉE (Encaissement) */}
        <Dialog open={!!quickEnc} onOpenChange={(o) => { if (!o) setQuickEnc(null); }}>
          <DialogContent className="max-w-lg" data-testid="quick-entree-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <Plus className="h-5 w-5" /> Ajouter une ENTRÉE (Encaissement)
              </DialogTitle>
            </DialogHeader>
            {quickEnc && (
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={quickEnc.date}
                         onChange={(e) => setQuickEnc({ ...quickEnc, date: e.target.value })}
                         data-testid="quick-entree-date" />
                </div>
                <div>
                  <Label>Catégorie *</Label>
                  <CategorySelect
                    value={quickEnc.categorie}
                    onChange={(v) => setQuickEnc({ ...quickEnc, categorie: v })}
                    type="produit"
                    defaultCategories={PRODUIT_CATEGORIES}
                    hiddenDefaults={hiddenProduitDefaults}
                    customCategories={customProduitCats}
                    onCategoriesChange={(nc, nh) => { setCustomProduitCats(nc); if (nh !== undefined) setHiddenProduitDefaults(nh); }}
                    triggerClassName="border-green-200"
                    testId="quick-entree-cat"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={quickEnc.description}
                         onChange={(e) => setQuickEnc({ ...quickEnc, description: e.target.value })}
                         placeholder="Détail de l'encaissement..."
                         data-testid="quick-entree-desc" />
                </div>
                <div>
                  <Label>Montant (FCFA) *</Label>
                  <Input type="number" min="0" step="0.01" value={quickEnc.montant}
                         onChange={(e) => setQuickEnc({ ...quickEnc, montant: parseFloat(e.target.value) || 0 })}
                         data-testid="quick-entree-montant" />
                </div>
              </div>
            )}
            <DialogFooter className="pt-4 border-t">
              <Button variant="outline" onClick={() => setQuickEnc(null)}>Annuler</Button>
              <Button onClick={() => submitQuick('enc')} disabled={savingQuick}
                      className="bg-green-600 hover:bg-green-700" data-testid="quick-entree-save">
                <Save className="h-4 w-4 mr-1" /> {savingQuick ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Quick-Add SORTIE (Décaissement) */}
        <Dialog open={!!quickDec} onOpenChange={(o) => { if (!o) setQuickDec(null); }}>
          <DialogContent className="max-w-lg" data-testid="quick-sortie-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <Plus className="h-5 w-5" /> Ajouter une SORTIE (Décaissement)
              </DialogTitle>
            </DialogHeader>
            {quickDec && (
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={quickDec.date}
                         onChange={(e) => setQuickDec({ ...quickDec, date: e.target.value })}
                         data-testid="quick-sortie-date" />
                </div>
                <div>
                  <Label>Catégorie *</Label>
                  <CategorySelect
                    value={quickDec.categorie}
                    onChange={(v) => setQuickDec({ ...quickDec, categorie: v })}
                    type="charge"
                    defaultCategories={CHARGE_CATEGORIES}
                    hiddenDefaults={hiddenChargeDefaults}
                    customCategories={customChargeCats}
                    onCategoriesChange={(nc, nh) => { setCustomChargeCats(nc); if (nh !== undefined) setHiddenChargeDefaults(nh); }}
                    triggerClassName="border-red-200"
                    testId="quick-sortie-cat"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={quickDec.description}
                         onChange={(e) => setQuickDec({ ...quickDec, description: e.target.value })}
                         placeholder="Détail du décaissement..."
                         data-testid="quick-sortie-desc" />
                </div>
                <div>
                  <Label>Montant (FCFA) *</Label>
                  <Input type="number" min="0" step="0.01" value={quickDec.montant}
                         onChange={(e) => setQuickDec({ ...quickDec, montant: parseFloat(e.target.value) || 0 })}
                         data-testid="quick-sortie-montant" />
                </div>
              </div>
            )}
            <DialogFooter className="pt-4 border-t">
              <Button variant="outline" onClick={() => setQuickDec(null)}>Annuler</Button>
              <Button onClick={() => submitQuick('dec')} disabled={savingQuick}
                      className="bg-red-600 hover:bg-red-700" data-testid="quick-sortie-save">
                <Save className="h-4 w-4 mr-1" /> {savingQuick ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
